import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { TestResult } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import { TextField } from "../../../ui/TextField";
import { showToast } from "../../../ui/toastStore";
import { ModelChoice } from "../shared/ModelChoice";
import { TestResultView } from "../shared/TestResultView";
import { useModelList } from "../shared/useModelList";

export const KIND_CHOICES = [
  { value: "anthropic", label: "Anthropic" },
  { value: "openai_compatible", label: "A service that works like OpenAI's (OpenAI-compatible)" },
] as const;

/**
 * An optional model key of the person's own (ui-direction screen 7). The person types the key and presses Connect; the service
 * says which models it offers and the person picks one (no model name is preset; a service with no list lets them type it).
 * The key is checked before it is saved and never shown again: the box starts empty every time, whatever the server says, and
 * is emptied after it is sent. The admin sees that a person uses their own key, never the key.
 */
export function PersonalKey() {
  const me = useCurrentMe();
  const queryClient = useQueryClient();
  const save = useApiCommand("set_personal_key");
  const remove = useApiCommand("remove_personal_key");
  const askModels = useApiCommand("list_my_models");
  const models = useModelList();
  const [kind, setKind] = useState<string>("anthropic");
  const [url, setUrl] = useState("");
  const [modelName, setModelName] = useState("");
  const [key, setKey] = useState("");
  const [result, setResult] = useState<TestResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const usingOwn = me.agent.uses_own_key;
  const typedKey = key.trim() !== "";
  const listed = models.state.phase === "listed" || models.state.phase === "no_list";
  const asking = models.state.phase === "loading";
  const ready = typedKey && listed && modelName.trim() !== "";
  const canSave = ready && !asking && !save.isPending;

  // The kind, the address or the key changed: what the service said last time no longer applies.
  const changed = (change: () => void) => {
    change();
    models.reset();
    setModelName("");
    setProblem(null);
    setResult(null);
  };

  const connect = async () => {
    if (!typedKey || asking) return;
    setProblem(null);
    setResult(null);
    await models.ask(() => askModels.mutateAsync({ kind, key: key.trim(), ...(url.trim() ? { url: url.trim() } : {}) }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setProblem(null);
    setResult(null);
    try {
      const reply = await save.mutateAsync({
        kind,
        key: key.trim(),
        model_name: modelName.trim(),
        ...(url.trim() ? { url: url.trim() } : {}),
      });
      setResult(reply.data);
      // Whatever the answer, the key is not kept on this screen, and the person connects again for the next try.
      setKey("");
      models.reset();
      if (reply.data.result === "connected") {
        setUrl("");
        setModelName("");
        void queryClient.invalidateQueries({ queryKey: ["me"] });
      }
    } catch (error) {
      setKey("");
      models.reset();
      setProblem(refusalText(error));
    }
  };

  const doRemove = async () => {
    setProblem(null);
    try {
      await remove.mutateAsync({});
      setConfirmRemove(false);
      setResult(null);
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      showToast({ text: "Your own key is removed. The agent uses the admin's connection again." });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <Card className="ra-section" aria-labelledby="ra-key-title">
      <h2 id="ra-key-title">Your own model key</h2>
      <p className="muted">
        Optional. By default the agent uses the connection the admin set. If you add a key of your own, the agent uses it for your questions. It is checked before it is saved, and it is never shown again.
      </p>
      <p role="status">
        <strong>{usingOwn ? "You are using your own key." : "You are using the admin's connection."}</strong>
      </p>

      <form className="stack" onSubmit={(event) => void submit(event)} autoComplete="off">
        <div className="field">
          <label htmlFor="ra-key-kind" className="field__label">
            Kind of service
          </label>
          <select id="ra-key-kind" className="field__input" value={kind} onChange={(event) => changed(() => setKind(event.target.value))}>
            {KIND_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        </div>
        <TextField
          label="Address (optional)"
          hint="Leave it empty to use the address the admin set."
          value={url}
          onChange={(event) => changed(() => setUrl(event.target.value))}
          inputMode="url"
          autoComplete="off"
        />
        <TextField
          label="Key"
          hint="Paste it here. It is write-only: it will not be shown again."
          type="password"
          value={key}
          onChange={(event) => changed(() => setKey(event.target.value))}
          // Enter in the key box asks for the models; when Save is ready it saves, as a form does.
          onKeyDown={(event) => {
            if (event.key !== "Enter" || canSave || !typedKey) return;
            event.preventDefault();
            void connect();
          }}
          autoComplete="off"
          spellCheck={false}
        />
        <div className="row">
          {/* Not disabled while the service is asked: the keyboard focus stays on the button, and a second press is ignored. */}
          <Button variant={listed ? "secondary" : "primary"} onClick={() => void connect()} aria-busy={asking || undefined} disabled={!typedKey || save.isPending} aria-describedby="ra-key-connect-hint">
            Connect
          </Button>
        </div>
        <p id="ra-key-connect-hint" className="muted">
          Paste the key, then press Connect. The service tells which models it offers, and you pick one.
        </p>
        <ModelChoice state={models.state} value={modelName} onChange={setModelName} hint="These are the models the service offers. The agent uses the one you pick for your questions." />
        <div className="row">
          <Button type="submit" variant="primary" busy={save.isPending} disabled={!canSave}>
            Check and save the key
          </Button>
          {usingOwn && (
            <Button onClick={() => setConfirmRemove(true)} disabled={remove.isPending}>
              Remove my key
            </Button>
          )}
        </div>
        {problem && (
          <p role="alert" className="form-error">
            {problem}
          </p>
        )}
      </form>

      {result && <TestResultView result={result} saved={{ good: "The key works and is saved. It will not be shown again.", bad: "The key was not saved." }} />}

      <ConfirmDialog
        open={confirmRemove}
        title="Remove your own key?"
        message="The agent will use the admin's connection again. You can add a key again later."
        confirmLabel="Remove my key"
        cancelLabel="Keep it"
        busy={remove.isPending}
        error={problem}
        onConfirm={() => void doRemove()}
        onCancel={() => setConfirmRemove(false)}
      />
    </Card>
  );
}
